import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { 
    DocumentTextIcon, 
    CalendarIcon, 
    ClipboardDocumentListIcon,
    AcademicCapIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    UserCircleIcon,
    XMarkIcon,
    Squares2X2Icon,
    ListBulletIcon,
    ArrowDownTrayIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { generateLessonPlanPDF } from '../../utils/pdfGenerators';
import DateRangePicker from '../common/DateRangePicker';
import FilterDropdown from '../common/FilterDropdown';
import PremiumTooltip from '../common/PremiumTooltip';
import ConfirmModal from '../common/ConfirmModal';
import LessonPlanModal from './LessonPlanModal';
import { useAppState } from '../../context/AppStateContext';

const LessonPlansTab = ({ courseId, isHOS = false, subTab = 'lesson-plans', onSubTabChange }) => {
    const { get, del } = useApi();
    const { showToast } = useAppState();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    
    // Core States
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Filter States
    const [selectedLearningArea, setSelectedLearningArea] = useState('all');
    const [selectedGradeStream, setSelectedGradeStream] = useState('all');
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const [selectedWeek, setSelectedWeek] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const [viewMode, setViewMode] = useState('grid');
    
    // Data States
    const [learningAreas, setLearningAreas] = useState([]);

    useEffect(() => {
        if (isHOS) {
            get('/api/cbc/learning-areas/')
                .then(res => setLearningAreas(res.results || res || []))
                .catch(err => console.error('Failed to fetch learning areas:', err));
        }
    }, [isHOS, get]);

    const { data: plans, isLoading } = useQuery({
        queryKey: isHOS ? ['lesson-plans', 'hos-all'] : ['lesson-plans', courseId],
        queryFn: () => get(isHOS ? '/api/cbc/lesson-plans/' : `/api/cbc/lesson-plans/?learning_area=${courseId}&course=${courseId}`),
        enabled: isHOS || !!courseId
    });

    // 1. Learning Area Filter Options (Pure subject names)
    const learningAreaOptions = useMemo(() => {
        const rawPlans = plans?.results || plans || [];
        const names = new Set();
        (learningAreas || []).forEach(la => { if (la.name) names.add(la.name); });
        rawPlans.forEach(p => { if (p.learning_area_name) names.add(p.learning_area_name); });
        return [
            { name: 'All Learning Areas', value: 'all' },
            ...Array.from(names).sort().map(name => ({ name, value: name }))
        ];
    }, [learningAreas, plans]);

    // 2. Grade & Stream Filter Options
    const gradeStreamOptions = useMemo(() => {
        const rawPlans = plans?.results || plans || [];
        const gradeMap = new Map();
        
        (learningAreas || []).forEach(la => {
            const gName = la.grade_level_name;
            const sName = la.stream_name || (la.stream_display_name && la.stream_display_name !== 'All Streams' ? la.stream_display_name : null);
            if (gName) {
                if (!gradeMap.has(gName)) gradeMap.set(gName, new Set());
                if (sName) gradeMap.get(gName).add(sName);
            }
        });

        rawPlans.forEach(p => {
            const gName = p.grade_level_name;
            const sName = p.stream_name;
            if (gName) {
                if (!gradeMap.has(gName)) gradeMap.set(gName, new Set());
                if (sName) gradeMap.get(gName).add(sName);
            }
        });

        const list = [{ name: 'All Grades & Streams', value: 'all' }];
        Array.from(gradeMap.keys()).sort().forEach(gName => {
            list.push({ name: `${gName} (All Streams)`, value: `grade_${gName}` });
            const streams = Array.from(gradeMap.get(gName)).sort();
            streams.forEach(sName => {
                list.push({ name: `${gName} • Stream ${sName}`, value: `stream_${gName}_${sName}` });
            });
        });
        return list;
    }, [learningAreas, plans]);

    // 3. Week Filter Options
    const weekOptions = useMemo(() => [
        { name: 'All Weeks', value: '' },
        ...[...Array(14)].map((_, i) => ({ name: `Week ${i + 1}`, value: String(i + 1) }))
    ], []);

    const hasActiveFilters = (selectedLearningArea !== 'all') || (selectedGradeStream !== 'all') || Boolean(dateRange?.from || dateRange?.to) || selectedWeek !== '';

    const handleResetFilters = () => {
        setSelectedLearningArea('all');
        setSelectedGradeStream('all');
        setDateRange({ from: null, to: null });
        setSelectedWeek('');
        setSearchQuery('');
        setCurrentPage(1);
    };

    const filteredPlans = useMemo(() => {
        return (plans?.results || plans || []).filter(p => {
            const title = p.title || '';
            const subStrand = p.sub_strand_name || '';
            const teacher = p.teacher_name || '';
            const laName = p.learning_area_name || '';
            const gradeName = p.grade_level_name || '';
            const streamName = p.stream_name || '';

            // Search query match
            if (searchQuery.trim()) {
                const searchStr = `${title} ${subStrand} ${teacher} ${laName} ${gradeName} ${streamName}`.toLowerCase();
                if (!searchStr.includes(searchQuery.toLowerCase())) return false;
            }

            // Learning Area filter
            if (selectedLearningArea && selectedLearningArea !== 'all') {
                if (laName.toLowerCase() !== selectedLearningArea.toLowerCase()) return false;
            }

            // Grade & Stream filter
            if (selectedGradeStream && selectedGradeStream !== 'all') {
                if (selectedGradeStream.startsWith('stream_')) {
                    const parts = selectedGradeStream.replace('stream_', '').split('_');
                    const targetGrade = parts[0]?.toLowerCase();
                    const targetStream = parts.slice(1).join('_').toLowerCase();
                    if (targetGrade && gradeName.toLowerCase() !== targetGrade) return false;
                    if (targetStream && (!streamName || !streamName.toLowerCase().includes(targetStream))) return false;
                } else if (selectedGradeStream.startsWith('grade_')) {
                    const targetGrade = selectedGradeStream.replace('grade_', '').toLowerCase();
                    if (gradeName.toLowerCase() !== targetGrade) return false;
                }
            }

            // Course ID filter if not HOS
            if (!isHOS && courseId && p.learning_area_id) {
                if (String(p.learning_area_id) !== String(courseId)) return false;
            }

            // Date Range filter
            const recordDate = p.lesson_date ? new Date(p.lesson_date) : null;
            if (recordDate && dateRange) {
                if (dateRange.from && recordDate < dateRange.from) return false;
                if (dateRange.to && recordDate > dateRange.to) return false;
            }

            // Week filter
            if (selectedWeek && String(p.week_number) !== String(selectedWeek)) {
                return false;
            }

            return true;
        });
    }, [plans, searchQuery, selectedLearningArea, selectedGradeStream, isHOS, dateRange, selectedWeek, courseId]);

    const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);
    const paginatedPlans = useMemo(() => {
        return filteredPlans.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    }, [filteredPlans, currentPage]);

    const handleExportPDF = async (planId, e) => {
        if (e) e.stopPropagation();
        try {
            const data = await get(`/api/cbc/lesson-plans/${planId}/`);
            await generateLessonPlanPDF(data);
            if (showToast) showToast('Lesson plan PDF exported successfully!', 'success');
        } catch (err) {
            console.error('Failed to export:', err);
            if (showToast) showToast(err?.response?.data?.error || 'Failed to export lesson plan.', 'error');
        }
    };

    // Opens the plan editor and tells it where the back button should return to
    const openPlanEditor = (planId) => {
        const plan = (plans?.results || plans || []).find(p => p.id === planId);
        const targetCourseId = courseId || plan?.learning_area_id;
        navigate(`/teacher/lesson-plan/editor/${planId}`, {
            state: targetCourseId
                ? { returnTo: `/teacher/courses/${targetCourseId}?tab=records&sub=lesson-plans` }
                : undefined
        });
    };

    // The trash buttons only ask for confirmation; the deletion itself runs from the modal
    const requestDeletePlan = (plan, e) => {
        if (e) e.stopPropagation();
        setPlanToDelete(plan);
    };

    const handleConfirmDeletePlan = async () => {
        if (!planToDelete) return;
        setIsDeleting(true);
        try {
            await del(`/api/cbc/lesson-plans/${planToDelete.id}/`);
            queryClient.invalidateQueries({ queryKey: ['lesson-plans'] });
            // Deleting a plan also deletes its Record of Work
            queryClient.invalidateQueries({ queryKey: ['records-of-work'] });
            if (showToast) showToast('Lesson plan deleted successfully.', 'success');
            setPlanToDelete(null);
        } catch (err) {
            console.error('Failed to delete lesson plan:', err);
            if (showToast) showToast(err?.response?.data?.error || 'Failed to delete lesson plan.', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <i className="fas fa-spinner fa-spin text-2xl text-indigo-600"></i>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-[1.5rem] px-6 py-5 border border-slate-100 shadow-sm gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600">
                            <ClipboardDocumentListIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                                Lesson Plans
                            </h2>
                            <p className="text-slate-500 font-medium text-xs mt-1">Manage and review your lesson plans.</p>
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
                    {!isHOS && (
                        <button
                            onClick={() => { setEditingPlan(null); setShowModal(true); }}
                            className="px-3.5 py-2.5 bg-[#18216D] hover:bg-indigo-900 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-sm transition-all whitespace-nowrap"
                        >
                            <PlusIcon className="w-4 h-4 stroke-[3]" />
                            <span>Plan</span>
                        </button>
                    )}

                    <div className="relative group flex-1 sm:w-72 max-w-xs">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                            <MagnifyingGlassIcon className="w-5 h-5" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search lesson plans..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-2 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-sm font-bold text-[#18216D]"
                        />
                    </div>
                    {isHOS && (
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`p-2 rounded-xl transition-all border shadow-sm flex items-center gap-2 ${
                                showFilters 
                                    ? 'bg-[#18216D] text-white border-transparent' 
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <FunnelIcon className="w-5 h-5" />
                            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Filters</span>
                        </button>
                    )}
                    
                    <div className="flex bg-slate-50 border border-slate-200 rounded-xl p-1 shadow-sm">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-[#18216D]'}`}
                            title="Grid View"
                        >
                            <Squares2X2Icon className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-1.5 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-[#18216D]'}`}
                            title="Table View"
                        >
                            <ListBulletIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {onSubTabChange && (
                <div className="flex justify-center border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8" aria-label="Records & Lesson Plans Tabs">
                        <button
                            type="button"
                            onClick={() => onSubTabChange('lesson-plans')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'lesson-plans' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <ClipboardDocumentListIcon className={`w-4 h-4 ${'lesson-plans' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Lesson Plans</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onSubTabChange('records')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'records' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <DocumentTextIcon className={`w-4 h-4 ${'records' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Records of Work</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onSubTabChange('rubrics')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'rubrics' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <AcademicCapIcon className={`w-4 h-4 ${'rubrics' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Assessment Rubrics</span>
                        </button>
                    </nav>
                </div>
            )}

            {/* Pop-Out Filters Drawer (Matching Classes & Grading Pattern) */}
            {showFilters && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 animate-fadeIn">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                            <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                Filter Lesson Plans
                            </span>
                            {hasActiveFilters && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                    Filtered
                                </span>
                            )}
                        </div>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* 1. Learning Area Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Learning Area
                            </label>
                            <FilterDropdown
                                value={selectedLearningArea}
                                onChange={(val) => { setSelectedLearningArea(val || 'all'); setCurrentPage(1); }}
                                options={learningAreaOptions}
                                placeholder="All Learning Areas"
                                className="w-full"
                            />
                        </div>

                        {/* 2. Grade & Stream Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Grade &amp; Stream
                            </label>
                            <FilterDropdown
                                value={selectedGradeStream}
                                onChange={(val) => { setSelectedGradeStream(val || 'all'); setCurrentPage(1); }}
                                options={gradeStreamOptions}
                                placeholder="All Grades &amp; Streams"
                                className="w-full"
                            />
                        </div>

                        {/* 3. Date Range Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Date Range
                            </label>
                            <DateRangePicker 
                                dateRange={dateRange}
                                onDateRangeChange={(val) => { setDateRange(val); setCurrentPage(1); }}
                            />
                        </div>

                        {/* 4. Week Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Week
                            </label>
                            <FilterDropdown
                                value={selectedWeek}
                                onChange={(val) => { setSelectedWeek(val || ''); setCurrentPage(1); }}
                                options={weekOptions}
                                placeholder="All Weeks"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            )}

            {filteredPlans.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
                    <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <DocumentTextIcon className="w-10 h-10 text-slate-300" />
                    </div>
                    <h3 className="text-xl font-bold text-[#18216D] mb-2">No Lesson Plans Found</h3>
                    <p className="text-slate-400 font-medium max-w-md mx-auto">
                        No lesson plans match your current filters.
                    </p>
                </div>
            ) : isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18216D]"></div>
                </div>
            ) : viewMode === 'grid' ? (
                <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {paginatedPlans.map((plan) => (
                            <div key={plan.id} className="bg-white rounded-[2.5rem] border border-slate-100 p-8 hover:shadow-[0_25px_60px_-15px_rgba(24,33,109,0.12)] hover:border-indigo-100 transition-all group flex flex-col h-full relative">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/30 rounded-bl-full rounded-tr-[2.5rem] pointer-events-none transition-transform group-hover:scale-125"></div>
                                <div className="absolute top-4 left-8 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[8px] font-black uppercase tracking-widest z-10">Lesson Plan</div>

                                {/* Top Right Card Actions: Edit & Delete (Hidden for HOS) */}
                                {!isHOS && (
                                    <div className="absolute top-4 right-6 flex items-center gap-1.5 z-20">
                                        <PremiumTooltip text="Edit Plan Details" allowWrap={false}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setEditingPlan(plan); setShowModal(true); }}
                                                className="p-1.5 bg-amber-50 hover:bg-amber-500 text-amber-700 hover:text-white rounded-lg border border-amber-200/80 transition-all shadow-sm"
                                            >
                                                <PencilIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                            </button>
                                        </PremiumTooltip>
                                        <PremiumTooltip text="Delete Lesson Plan" allowWrap={false}>
                                            <button
                                                onClick={(e) => requestDeletePlan(plan, e)}
                                                className="p-1.5 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white rounded-lg border border-red-200/80 transition-all shadow-sm"
                                            >
                                                <TrashIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                            </button>
                                        </PremiumTooltip>
                                    </div>
                                )}

                                <div className="mt-12 relative z-10"></div>

                                <div className="flex-1 relative z-10">
                                    <PremiumTooltip text={plan.title || 'Untitled Lesson Plan'} allowWrap={true} wrapperClassName="relative inline-flex max-w-full z-20">
                                        <h3 className="text-base font-black text-[#18216D] line-clamp-2 mb-4 tracking-tight leading-snug italic">
                                            {plan.title || 'Untitled Lesson Plan'}
                                        </h3>
                                    </PremiumTooltip>
                                    
                                    <div className="flex flex-col gap-3 mb-6">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#FFC425] shrink-0"></span>
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">
                                                    {plan.learning_area_name}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                                                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.15em] italic">
                                                    {plan.grade_level_name} {plan.stream_name ? `- ${plan.stream_name}` : ''}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                                <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.15em] italic">
                                                    {plan.lesson_date ? new Date(plan.lesson_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unscheduled'}
                                                </span>
                                            </div>
                                            <div className="w-px h-3 bg-slate-200" />
                                            <div className="flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                                <span className="text-[10px] font-black text-purple-600 uppercase tracking-[0.15em] italic">
                                                    Week {plan.week_number || 'N/A'}
                                                </span>
                                            </div>
                                        </div>

                                        {isHOS && plan.teacher_name && (
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                                <span className="text-[10px] font-black text-amber-600 uppercase tracking-[0.15em] italic truncate">
                                                    {plan.teacher_name}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-6 pt-4 border-t border-slate-50 relative z-10 flex gap-2">
                                    <button
                                        onClick={() => openPlanEditor(plan.id)}
                                        className="flex-1 flex items-center justify-center gap-3 py-3 bg-indigo-50 hover:bg-indigo-100 text-[#18216D] rounded-xl text-xs font-black transition-colors uppercase tracking-[0.2em]"
                                    >
                                        View Plan
                                        <ChevronRightIcon className="w-5 h-5 stroke-[3]" />
                                    </button>
                                    <PremiumTooltip text="Download PDF" allowWrap={false}>
                                        <button
                                            onClick={() => handleExportPDF(plan.id)}
                                            className="p-3 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all border border-transparent shadow-sm flex-none"
                                        >
                                            <ArrowDownTrayIcon className="w-5 h-5 stroke-[2]" />
                                        </button>
                                    </PremiumTooltip>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Week</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Date</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[200px]">Title</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Learning Area</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Grade & Stream</th>
                                    {isHOS && <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Teacher</th>}
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {paginatedPlans.map((plan) => (
                                    <tr key={plan.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="py-4 px-6">
                                            <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                                                W{plan.week_number || '?'}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex items-center gap-1.5 text-slate-500 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                                                <CalendarIcon className="w-3.5 h-3.5" />
                                                {plan.lesson_date || 'Unscheduled'}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <PremiumTooltip text={plan.title || 'Untitled Lesson Plan'} allowWrap={true} wrapperClassName="relative inline-flex max-w-full">
                                                <h3 className="text-sm font-black text-[#18216D] leading-tight line-clamp-1">
                                                    {plan.title || 'Untitled Lesson Plan'}
                                                </h3>
                                            </PremiumTooltip>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                                {plan.learning_area_name}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                                {plan.grade_level_name} {plan.stream_name ? `(${plan.stream_name})` : ''}
                                            </span>
                                        </td>
                                        {isHOS && (
                                            <td className="py-4 px-6">
                                                {plan.teacher_name && (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                                                            <UserCircleIcon className="w-4 h-4 text-amber-500" />
                                                        </div>
                                                        <span className="text-xs font-black text-[#18216D] whitespace-nowrap">
                                                            {plan.teacher_name}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                        <td className="py-4 px-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => openPlanEditor(plan.id)}
                                                    className="px-4 py-2 bg-white text-[#18216D] hover:bg-[#18216D] hover:text-white transition-all rounded-xl text-[10px] font-black uppercase tracking-widest border border-slate-200 hover:border-transparent shadow-sm whitespace-nowrap"
                                                >
                                                    View Plan
                                                </button>
                                                {!isHOS && (
                                                    <>
                                                        <button
                                                            onClick={() => { setEditingPlan(plan); setShowModal(true); }}
                                                            className="p-2 bg-amber-50 hover:bg-amber-500 text-amber-700 hover:text-white rounded-xl border border-amber-200/80 transition-all shadow-sm"
                                                            title="Edit Plan Details"
                                                        >
                                                            <PencilIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => requestDeletePlan(plan, e)}
                                                            className="p-2 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white rounded-xl border border-red-200/80 transition-all shadow-sm"
                                                            title="Delete Plan"
                                                        >
                                                            <TrashIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {filteredPlans.length > 0 && totalPages > 1 && (
                <div className="bg-white rounded-[1.5rem] p-4 border border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm mt-6">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-4">
                        Showing page {currentPage} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2.5 bg-slate-50 border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <div className="flex gap-1">
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentPage(i + 1)}
                                    className={`w-9 h-9 rounded-xl text-[10px] font-black transition-all ${currentPage === i + 1
                                        ? 'bg-[#18216D] text-white shadow-md'
                                        : 'bg-slate-50 text-slate-400 hover:text-[#18216D] border border-slate-100 hover:bg-slate-100'
                                        }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="p-2.5 bg-slate-50 border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {showModal && (
                <LessonPlanModal
                    courseId={courseId}
                    editPlan={editingPlan}
                    onClose={() => { setShowModal(false); setEditingPlan(null); }}
                    onEditSuccess={() => {
                        setShowModal(false);
                        setEditingPlan(null);
                        queryClient.invalidateQueries({ queryKey: ['lesson-plans'] });
                        queryClient.invalidateQueries({ queryKey: ['records-of-work'] });
                    }}
                />
            )}

            <ConfirmModal
                isOpen={Boolean(planToDelete)}
                onClose={() => { if (!isDeleting) setPlanToDelete(null); }}
                onConfirm={handleConfirmDeletePlan}
                loading={isDeleting}
                title="Delete Lesson Plan"
                message={`Are you sure you want to delete "${planToDelete?.title || 'this lesson plan'}"? Its Record of Work is deleted with it. This action cannot be undone.`}
                confirmText="Delete Plan"
            />
        </div>
    );
};

export default LessonPlansTab;
